import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { db } from '../../firebase';
import { collection, query, orderBy, getDocs, limit as firestoreLimit, doc, getDoc } from 'firebase/firestore';

// Determine API base URL (kept consistent with Messages.js)
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (!envUrl) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('your-cloud-run-url') || envUrl.includes('placeholder')) {
    return process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8081';
  }
  if (envUrl.includes('run.app')) {
    return envUrl;
  }
  if (process.env.NODE_ENV === 'production') {
    return envUrl;
  }
  return 'http://localhost:8081';
};

const API_BASE_URL = getApiBaseUrl();

export default function AdminPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [health, setHealth] = useState(null);
  const [users, setUsers] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [deletingUid, setDeletingUid] = useState('');
  const [actingUid, setActingUid] = useState('');
  const [echoInput, setEchoInput] = useState('{"hello":"world"}');
  const [echoResult, setEchoResult] = useState(null);
  const [apiUrlEcho, setApiUrlEcho] = useState(null);
  const [error, setError] = useState('');

  // API Testing state
  const [testMethod, setTestMethod] = useState('GET');
  const [testEndpoint, setTestEndpoint] = useState('/api/me');
  const [testQueryParams, setTestQueryParams] = useState('');
  const [testBody, setTestBody] = useState('{}');
  const [testResponse, setTestResponse] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testHistory, setTestHistory] = useState([]);

  // Login logs state
  const [loginLogs, setLoginLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // ID registry tools
  const [idRegistryInput, setIdRegistryInput] = useState('');
  const [idRegistryHashInput, setIdRegistryHashInput] = useState('');
  const [idRegistryExpectedUid, setIdRegistryExpectedUid] = useState('');
  const [idRegistryNote, setIdRegistryNote] = useState('');
  const [idRegistryResult, setIdRegistryResult] = useState(null);
  const [idRegistryLoading, setIdRegistryLoading] = useState(false);

  // ESP32 servo control
  const SERVO_STATES = ['passive', 'free', 'busy'];
  const [servoState, setServoState] = useState('passive');
  const [servoSendEmail, setServoSendEmail] = useState(true);
  const [servoLoading, setServoLoading] = useState(false);
  const [servoResult, setServoResult] = useState(null);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      try {
        const token = await currentUser.getIdToken();
        const resp = await axios.get(`${API_BASE_URL}/api/user/roles`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const roles = (resp.data && resp.data.roles) || [];
        const isAdm = roles.map(String).map(r => r.toLowerCase()).includes('admin');
        setIsAdmin(isAdm);
        if (!isAdm) {
          navigate('/jobs');
        }
      } catch (e) {
        setError('Failed to verify admin role');
      } finally {
        setChecking(false);
      }
    };
    checkAdmin();
  }, [currentUser, navigate]);

  if (checking) {
    return <div style={{ padding: 24 }}>Checking admin access…</div>;
  }

  if (!isAdmin) {
    return null;
  }

  const callHealth = async () => {
    setError('');
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const resp = await axios.get(`${API_BASE_URL}/api/health`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { api_url: apiUrl }
      });
      setHealth(resp.data);
    } catch (e) {
      setError(`Health failed: ${e?.response?.data?.error || e.message}`);
      setHealth(null);
    }
  };

  const callListUsers = async () => {
    setError('');
    setLoadingUsers(true);
    try {
      const token = await currentUser.getIdToken();
      const resp = await axios.get(`${API_BASE_URL}/api/admin/list-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(resp.data);
    } catch (e) {
      setError(`List users failed: ${e?.response?.data?.error || e.message}`);
      setUsers(null);
    } finally {
      setLoadingUsers(false);
    }
  };

  const callEcho = async () => {
    setError('');
    try {
      const token = await currentUser.getIdToken();
      let parsed;
      try {
        parsed = echoInput ? JSON.parse(echoInput) : {};
      } catch (parseErr) {
        setError('Echo input must be valid JSON');
        return;
      }
      const resp = await axios.post(`${API_BASE_URL}/api/admin/debug/echo`, parsed, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEchoResult(resp.data);
    } catch (e) {
      setError(`Echo failed: ${e?.response?.data?.error || e.message}`);
      setEchoResult(null);
    }
  };

  const echoApiUrl = () => {
    // Show both the raw env variable and the effective base URL used by the app
    const raw = process.env.REACT_APP_API_URL || '';
    setApiUrlEcho({
      rawEnv: raw,
      effectiveBaseUrl: API_BASE_URL,
      nodeEnv: process.env.NODE_ENV
    });
  };

  const deleteUser = async (uid, opts = { purge: true }) => {
    if (!uid) return;
    const confirmText = opts.purge
      ? 'Are you sure you want to permanently delete this user and purge their data?'
      : 'Are you sure you want to permanently delete this user?';
    if (!window.confirm(confirmText)) return;
    setError('');
    setDeletingUid(uid);
    try {
      const token = await currentUser.getIdToken();
      const resp = await axios.post(`${API_BASE_URL}/api/admin/delete-user`, {
        uid,
        purge: !!opts.purge
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local list if present
      if (users && Array.isArray(users.users)) {
        setUsers({
          users: users.users.filter(u => u.uid !== uid)
        });
      }
      return resp.data;
    } catch (e) {
      setError(`Delete user failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setDeletingUid('');
    }
  };

  const adminPost = async (path, body) => {
    const token = await currentUser.getIdToken();
    return axios.post(`${API_BASE_URL}${path}`, body || {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
  };

  const adminBlockUser = async (uid) => {
    if (!uid) return;
    if (!window.confirm(`Block user ${uid}?`)) return;
    const reason = window.prompt('Block reason (optional):', 'admin_block') || 'admin_block';
    const note = window.prompt('Admin note (optional):', '') || '';
    setError('');
    setActingUid(uid);
    try {
      await adminPost('/api/admin/users/block', { uid, reason, note });
      await callListUsers();
    } catch (e) {
      setError(`Block user failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setActingUid('');
    }
  };

  const adminUnblockUser = async (uid) => {
    if (!uid) return;
    if (!window.confirm(`Unblock user ${uid}?`)) return;
    const note = window.prompt('Admin note (optional):', '') || '';
    setError('');
    setActingUid(uid);
    try {
      await adminPost('/api/admin/users/unblock', { uid, note });
      await callListUsers();
    } catch (e) {
      setError(`Unblock user failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setActingUid('');
    }
  };

  const adminApproveKyc = async (uid, kycIdLast4) => {
    if (!uid) return;
    if (!window.confirm(`Approve KYC for ${uid}?`)) return;
    const note = window.prompt('Admin note (optional):', '') || '';
    // Optional: allow manual ID number entry if OCR didn't capture it
    const idNumber = window.prompt(
      'If KYC ID is missing, enter full ID number to approve (optional):',
      ''
    ) || '';
    setError('');
    setActingUid(uid);
    try {
      await adminPost('/api/admin/kyc/approve', { uid, note, idNumber: idNumber.trim() || undefined });
      await callListUsers();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      setError(`Approve KYC failed: ${msg}`);
    } finally {
      setActingUid('');
    }
  };

  const adminRejectKyc = async (uid) => {
    if (!uid) return;
    if (!window.confirm(`Reject KYC for ${uid}?`)) return;
    const note = window.prompt('Admin note (optional):', '') || '';
    setError('');
    setActingUid(uid);
    try {
      await adminPost('/api/admin/kyc/reject', { uid, note });
      await callListUsers();
    } catch (e) {
      setError(`Reject KYC failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setActingUid('');
    }
  };

  const lookupIdRegistry = async () => {
    setError('');
    setIdRegistryLoading(true);
    setIdRegistryResult(null);
    try {
      const payload = {};
      if (idRegistryHashInput.trim()) payload.idHash = idRegistryHashInput.trim();
      if (idRegistryInput.trim()) payload.idNumber = idRegistryInput.trim();
      const resp = await adminPost('/api/admin/kyc/id-registry/lookup', payload);
      setIdRegistryResult(resp.data);
    } catch (e) {
      setError(`ID registry lookup failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setIdRegistryLoading(false);
    }
  };

  const releaseIdRegistry = async () => {
    if (!window.confirm('Release this ID from the registry? This is a support action.')) return;
    setError('');
    setIdRegistryLoading(true);
    setIdRegistryResult(null);
    try {
      const payload = {
        idHash: idRegistryHashInput.trim() || undefined,
        idNumber: idRegistryInput.trim() || undefined,
        expectedUid: idRegistryExpectedUid.trim() || undefined,
        reason: 'admin_release',
        note: idRegistryNote.trim() || undefined,
      };
      const resp = await adminPost('/api/admin/kyc/id-registry/release', payload);
      setIdRegistryResult(resp.data);
    } catch (e) {
      setError(`ID registry release failed: ${e?.response?.data?.error || e.message}`);
    } finally {
      setIdRegistryLoading(false);
    }
  };

  const setServoStateApi = async () => {
    setError('');
    setServoLoading(true);
    setServoResult(null);
    try {
      const resp = await adminPost('/api/admin/servo/set-state', { state: servoState, sendEmail: servoSendEmail });
      setServoResult(resp.data);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      setError(`Servo failed: ${msg}`);
      setServoResult(e?.response?.data || { success: false, error: msg });
    } finally {
      setServoLoading(false);
    }
  };

  const filteredUsers = (() => {
    const list = (users && Array.isArray(users.users)) ? users.users : [];
    const q = (searchEmail || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter(u => (u.email || '').toLowerCase().includes(q));
  })();

  // Generic API test function
  const testApiRequest = async (method, endpoint, queryParams = '', body = null) => {
    setError('');
    setTestLoading(true);
    setTestResponse(null);

    try {
      const token = await currentUser.getIdToken();
      const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

      // Parse query params
      const params = {};
      if (queryParams.trim()) {
        queryParams.split('&').forEach(param => {
          const [key, value] = param.split('=').map(s => decodeURIComponent(s.trim()));
          if (key) params[key] = value || '';
        });
      }

      // Parse body if provided
      let requestBody = null;
      if (body && body.trim()) {
        try {
          requestBody = JSON.parse(body);
        } catch (e) {
          setError('Request body must be valid JSON');
          setTestLoading(false);
          return;
        }
      }

      const config = {
        method: method.toLowerCase(),
        url,
        headers: { Authorization: `Bearer ${token}` },
        params: Object.keys(params).length > 0 ? params : undefined,
        data: requestBody,
      };

      const startTime = Date.now();
      const resp = await axios(config);
      const duration = Date.now() - startTime;

      const responseData = {
        status: resp.status,
        statusText: resp.statusText,
        headers: resp.headers,
        data: resp.data,
        duration: `${duration}ms`,
        timestamp: new Date().toISOString(),
      };

      setTestResponse(responseData);

      // Add to history (keep last 10)
      setTestHistory(prev => [
        { method, endpoint, queryParams, body, response: responseData },
        ...prev.slice(0, 9)
      ]);

    } catch (e) {
      const duration = e.response ? 'N/A' : `${Date.now() - (Date.now() - 100)}ms`;
      const errorResponse = {
        error: true,
        status: e.response?.status || 'Network Error',
        statusText: e.response?.statusText || e.message,
        headers: e.response?.headers || {},
        data: e.response?.data || { message: e.message },
        duration,
        timestamp: new Date().toISOString(),
      };
      setTestResponse(errorResponse);
      setError(`Request failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestRequest = () => {
    testApiRequest(testMethod, testEndpoint, testQueryParams, testBody);
  };

  // Fetch login logs from Firestore
  const fetchLoginLogs = async () => {
    setError('');
    setLoadingLogs(true);
    try {
      const loginLogsRef = collection(db, 'loginLogs');
      const q = query(
        loginLogsRef,
        orderBy('loginTimestamp', 'desc'),
        firestoreLimit(1000) // Limit to 1000 most recent logs
      );
      const querySnapshot = await getDocs(q);
      const logs = [];

      // Process logs and enrich with user data if needed
      for (const logDoc of querySnapshot.docs) {
        const data = logDoc.data();
        let enrichedData = {
          id: logDoc.id,
          ...data,
          // Convert Firestore timestamp to Date if needed
          loginTimestamp: data.loginTimestamp?.toDate ? data.loginTimestamp.toDate() : data.loginTimestamp
        };

        // If displayName or photoURL is null/missing, try to fetch from user document
        // if ((!enrichedData.displayName || !enrichedData.photoURL) && enrichedData.userId) {
        //   try {
        //     const userDocRef = doc(db, 'users', enrichedData.userId);
        //     const userDocSnap = await getDoc(userDocRef);
        //     if (userDocSnap.exists()) {
        //       const userData = userDocSnap.data();
        //       // Only override if the login log value is null/undefined
        //       if (!enrichedData.displayName && userData.displayName) {
        //         enrichedData.displayName = userData.displayName;
        //       }
        //       if (!enrichedData.photoURL && userData.photoURL) {
        //         enrichedData.photoURL = userData.photoURL;
        //       }
        //     }
        //   } catch (userFetchError) {
        //     // Silently continue if user fetch fails - not critical
        //     console.warn(`Failed to fetch user data for ${enrichedData.userId}:`, userFetchError);
        //   }
        // }

        logs.push(enrichedData);
      }

      setLoginLogs(logs);
    } catch (e) {
      setError(`Failed to fetch login logs: ${e.message}`);
      setLoginLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Quick test functions for common endpoints
  const quickTests = {
    me: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/me');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/me', '', null);
    },
    roles: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/user/roles');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/user/roles', '', null);
    },
    profile: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/user/profile');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/user/profile', '', null);
    },
    freelancerLevel: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/freelancer/level');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/freelancer/level', '', null);
    },
    freelancerHome: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/freelancer/home');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/freelancer/home', '', null);
    },
    employerHome: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/employer/home');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/employer/home', '', null);
    },
    searchUsers: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/chat/search-users');
      setTestQueryParams('query=test');
      setTestBody('{}');
      testApiRequest('GET', '/api/chat/search-users', 'query=test', null);
    },
    availableUsers: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/chat/available-users');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/chat/available-users', '', null);
    },
    unreadCount: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/chat/unread-count');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/chat/unread-count', '', null);
    },
    conversations: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/chat/conversations');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/chat/conversations', '', null);
    },
    israelCities: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/location/israel-cities');
      setTestQueryParams('limit=10');
      setTestBody('{}');
      testApiRequest('GET', '/api/location/israel-cities', 'limit=10', null);
    },
    debugRoutes: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/admin/debug/routes');
      setTestQueryParams('');
      setTestBody('{}');
      testApiRequest('GET', '/api/admin/debug/routes', '', null);
    },
    calc: () => {
      setTestMethod('GET');
      setTestEndpoint('/api/calc');
      setTestQueryParams('a=5&b=3');
      setTestBody('{}');
      testApiRequest('GET', '/api/calc', 'a=5&b=3', null);
    },
  };

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Admin Tools</h2>
        <button onClick={() => navigate('/jobs')} style={{ padding: '8px 12px' }}>
          Back to Jobs
        </button>
      </div>
      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <section style={{ marginBottom: 24 }}>
        <h3>Environment</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={echoApiUrl} style={{ padding: '8px 12px' }}>
            Echo REACT_APP_API_URL
          </button>
          <span style={{ color: '#6b7280' }}>(Shows raw env and effective API base URL)</span>
        </div>
        {apiUrlEcho && (
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f3f4f6', padding: 12, borderRadius: 6, marginTop: 12 }}>
{JSON.stringify(apiUrlEcho, null, 2)}
          </pre>
        )}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3>Health Check</h3>
        <button onClick={callHealth} style={{ padding: '8px 12px' }}>GET /api/health</button>
        {health && (
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f3f4f6', padding: 12, borderRadius: 6, marginTop: 12 }}>
{JSON.stringify(health, null, 2)}
          </pre>
        )}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3>ESP32 Servo</h3>
        <p style={{ color: '#6b7280', marginTop: 0 }}>
          Set the servo state (via AWS IoT). States: passive, free, busy. Setting to free or busy sends an email to SERVO_NOTIFY_EMAIL_LIST.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 14 }}>State:</label>
            <select
              value={servoState}
              onChange={(e) => setServoState(e.target.value)}
              style={{ padding: '6px 10px', fontFamily: 'monospace', minWidth: 100 }}
            >
              {SERVO_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={servoSendEmail}
              onChange={(e) => setServoSendEmail(e.target.checked)}
            />
            Send email (when free/busy)
          </label>
          <button
            onClick={setServoStateApi}
            disabled={servoLoading}
            style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: 4, fontWeight: 'bold' }}
          >
            {servoLoading ? 'Sending…' : 'Set state'}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {SERVO_STATES.map((s) => (
              <button
                key={s}
                onClick={() => setServoState(s)}
                style={{
                  padding: '6px 10px',
                  fontSize: '0.85em',
                  background: servoState === s ? '#059669' : '#e5e7eb',
                  color: servoState === s ? 'white' : '#374151',
                  border: `1px solid ${servoState === s ? '#047857' : '#d1d5db'}`,
                  borderRadius: 4,
                }}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
        {servoResult && (
          <pre style={{ whiteSpace: 'pre-wrap', background: servoResult.success ? '#ecfdf5' : '#fef2f2', color: servoResult.success ? '#065f46' : '#991b1b', padding: 12, borderRadius: 6, marginTop: 12 }}>
            {JSON.stringify(servoResult, null, 2)}
          </pre>
        )}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3>Users</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={callListUsers} style={{ padding: '8px 12px' }} disabled={loadingUsers}>
            {loadingUsers ? 'Loading…' : 'GET /api/admin/list-users'}
          </button>
          <input
            type="text"
            placeholder="Search by email…"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            style={{ padding: 8, minWidth: 260 }}
          />
        </div>
        {users && (
          <div style={{ marginTop: 12 }}>
            {filteredUsers.length === 0 ? (
              <div style={{ color: '#6b7280' }}>No users match the search.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Email</th>
                      <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>UID</th>
                      <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Auth Disabled</th>
                      <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>KYC</th>
                      <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.uid}>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>{u.email || <em>(no email)</em>}</td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace' }}>{u.uid}</td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                          {u.disabled ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>Yes</span> : 'No'}
                        </td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                          <div style={{ fontSize: 12, color: '#374151' }}>
                            <div><strong>Status:</strong> {u.kycStatus || <em>n/a</em>}</div>
                            <div><strong>Completed:</strong> {String(!!u.kycCompleted)}</div>
                            <div><strong>EmailBlocked:</strong> {String(!!u.kycEmailBlocked)}</div>
                            <div><strong>ID last4:</strong> {u.kycIdLast4 || <em>n/a</em>}</div>
                            {u.kycBlockedReason && <div><strong>Reason:</strong> {u.kycBlockedReason}</div>}
                          </div>
                        </td>
                        <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                            <button
                              onClick={() => adminApproveKyc(u.uid, u.kycIdLast4)}
                              disabled={actingUid === u.uid}
                              style={{ padding: '6px 10px', background: '#10b981', color: 'white', border: 'none', borderRadius: 4 }}
                            >
                              {actingUid === u.uid ? 'Working…' : 'Approve KYC'}
                            </button>
                            <button
                              onClick={() => adminRejectKyc(u.uid)}
                              disabled={actingUid === u.uid}
                              style={{ padding: '6px 10px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 4 }}
                            >
                              Reject KYC
                            </button>
                            <button
                              onClick={() => adminBlockUser(u.uid)}
                              disabled={actingUid === u.uid}
                              style={{ padding: '6px 10px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 4 }}
                            >
                              Block
                            </button>
                            <button
                              onClick={() => adminUnblockUser(u.uid)}
                              disabled={actingUid === u.uid}
                              style={{ padding: '6px 10px', background: '#334155', color: 'white', border: 'none', borderRadius: 4 }}
                            >
                              Unblock
                            </button>
                          <button
                            onClick={() => deleteUser(u.uid, { purge: true })}
                            disabled={deletingUid === u.uid}
                            style={{ padding: '6px 10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: 4 }}
                          >
                            {deletingUid === u.uid ? 'Deleting…' : 'Delete (purge)'}
                          </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3>KYC ID Registry</h3>
        <p style={{ color: '#6b7280', marginTop: 0 }}>
          Lookup or release an ID hash from the global registry (support tool for duplicate-ID cases).
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>ID Number (optional)</label>
            <input
              value={idRegistryInput}
              onChange={(e) => setIdRegistryInput(e.target.value)}
              placeholder="e.g. 123456789"
              style={{ width: '100%', padding: 8, fontFamily: 'monospace' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>ID Hash (optional)</label>
            <input
              value={idRegistryHashInput}
              onChange={(e) => setIdRegistryHashInput(e.target.value)}
              placeholder="sha256 hash…"
              style={{ width: '100%', padding: 8, fontFamily: 'monospace' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Expected UID (release safety)</label>
            <input
              value={idRegistryExpectedUid}
              onChange={(e) => setIdRegistryExpectedUid(e.target.value)}
              placeholder="optional"
              style={{ width: '100%', padding: 8, fontFamily: 'monospace' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Note (optional)</label>
            <input
              value={idRegistryNote}
              onChange={(e) => setIdRegistryNote(e.target.value)}
              placeholder="support note…"
              style={{ width: '100%', padding: 8 }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <button
            onClick={lookupIdRegistry}
            disabled={idRegistryLoading}
            style={{ padding: '8px 12px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4 }}
          >
            {idRegistryLoading ? 'Working…' : 'Lookup'}
          </button>
          <button
            onClick={releaseIdRegistry}
            disabled={idRegistryLoading}
            style={{ padding: '8px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: 4 }}
          >
            {idRegistryLoading ? 'Working…' : 'Release'}
          </button>
        </div>
        {idRegistryResult && (
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f3f4f6', padding: 12, borderRadius: 6, marginTop: 12 }}>
{JSON.stringify(idRegistryResult, null, 2)}
          </pre>
        )}
      </section>

      <section style={{ marginBottom: 24 }}>
        <h3>Echo</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <textarea
            value={echoInput}
            onChange={(e) => setEchoInput(e.target.value)}
            rows={6}
            style={{ width: '100%', fontFamily: 'monospace' }}
          />
          <button onClick={callEcho} style={{ padding: '8px 12px', height: 40 }}>POST /api/admin/debug/echo</button>
        </div>
        {echoResult && (
          <pre style={{ whiteSpace: 'pre-wrap', background: '#f3f4f6', padding: 12, borderRadius: 6, marginTop: 12 }}>
{JSON.stringify(echoResult, null, 2)}
          </pre>
        )}
      </section>

      <section style={{ marginBottom: 24, borderTop: '2px solid #e5e7eb', paddingTop: 24 }}>
        <h3>API Testing</h3>
        <p style={{ color: '#6b7280', marginBottom: 16 }}>
          Test any API endpoint with custom requests. All requests are authenticated with your admin token.
        </p>

        {/* Quick Test Buttons */}
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 12, fontSize: '0.9em', color: '#374151' }}>Quick Tests</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button onClick={quickTests.me} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/me
            </button>
            <button onClick={quickTests.roles} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/user/roles
            </button>
            <button onClick={quickTests.profile} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/user/profile
            </button>
            <button onClick={quickTests.freelancerLevel} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/freelancer/level
            </button>
            <button onClick={quickTests.freelancerHome} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#10b981', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/freelancer/home
            </button>
            <button onClick={quickTests.employerHome} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#10b981', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/employer/home
            </button>
            <button onClick={quickTests.searchUsers} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/chat/search-users
            </button>
            <button onClick={quickTests.availableUsers} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/chat/available-users
            </button>
            <button onClick={quickTests.unreadCount} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/chat/unread-count
            </button>
            <button onClick={quickTests.conversations} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#8b5cf6', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/chat/conversations
            </button>
            <button onClick={quickTests.israelCities} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/location/israel-cities
            </button>
            <button onClick={quickTests.debugRoutes} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#ef4444', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/admin/debug/routes
            </button>
            <button onClick={quickTests.calc} style={{ padding: '6px 10px', fontSize: '0.85em', background: '#06b6d4', color: 'white', border: 'none', borderRadius: 4 }}>
              GET /api/calc
            </button>
          </div>
        </div>

        {/* Custom Request Builder */}
        <div style={{ background: '#f9fafb', padding: 16, borderRadius: 8, marginBottom: 16 }}>
          <h4 style={{ marginBottom: 12, fontSize: '0.9em', color: '#374151' }}>Custom Request</h4>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <select
              value={testMethod}
              onChange={(e) => setTestMethod(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db' }}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
            <input
              type="text"
              value={testEndpoint}
              onChange={(e) => setTestEndpoint(e.target.value)}
              placeholder="/api/endpoint"
              style={{ flex: 1, minWidth: 200, padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db', fontFamily: 'monospace' }}
            />
            <button
              onClick={handleTestRequest}
              disabled={testLoading}
              style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4, fontWeight: 'bold' }}
            >
              {testLoading ? 'Sending...' : 'Send Request'}
            </button>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em', color: '#6b7280' }}>
              Query Parameters (e.g., key1=value1&key2=value2)
            </label>
            <input
              type="text"
              value={testQueryParams}
              onChange={(e) => setTestQueryParams(e.target.value)}
              placeholder="key1=value1&key2=value2"
              style={{ width: '100%', padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db', fontFamily: 'monospace', fontSize: '0.9em' }}
            />
          </div>

          {(testMethod === 'POST' || testMethod === 'PUT' || testMethod === 'PATCH') && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: '0.85em', color: '#6b7280' }}>
                Request Body (JSON)
              </label>
              <textarea
                value={testBody}
                onChange={(e) => setTestBody(e.target.value)}
                rows={6}
                placeholder='{"key": "value"}'
                style={{ width: '100%', padding: '8px', borderRadius: 4, border: '1px solid #d1d5db', fontFamily: 'monospace', fontSize: '0.9em' }}
              />
            </div>
          )}
        </div>

        {/* Response Display */}
        {testResponse && (
          <div style={{ marginTop: 16 }}>
            <h4 style={{ marginBottom: 12, fontSize: '0.9em', color: '#374151' }}>Response</h4>
            <div style={{ background: '#f3f4f6', padding: 12, borderRadius: 6, marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: '0.85em' }}>
                <span>
                  <strong>Status:</strong>{' '}
                  <span style={{ color: testResponse.error ? '#ef4444' : testResponse.status >= 200 && testResponse.status < 300 ? '#10b981' : '#f59e0b' }}>
                    {testResponse.status} {testResponse.statusText}
                  </span>
                </span>
                <span><strong>Duration:</strong> {testResponse.duration}</span>
                <span><strong>Time:</strong> {new Date(testResponse.timestamp).toLocaleTimeString()}</span>
              </div>
            </div>
            <pre style={{ whiteSpace: 'pre-wrap', background: '#1f2937', color: '#f9fafb', padding: 16, borderRadius: 6, overflow: 'auto', maxHeight: '500px', fontSize: '0.85em' }}>
{JSON.stringify(testResponse.data || testResponse, null, 2)}
            </pre>
          </div>
        )}

        {/* Request History */}
        {testHistory.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h4 style={{ marginBottom: 12, fontSize: '0.9em', color: '#374151' }}>Recent Requests</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {testHistory.slice(0, 5).map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setTestMethod(item.method);
                    setTestEndpoint(item.endpoint);
                    setTestQueryParams(item.queryParams);
                    setTestBody(item.body || '{}');
                  }}
                  style={{
                    background: '#f9fafb',
                    padding: 12,
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    fontSize: '0.85em'
                  }}
                >
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: item.method === 'GET' ? '#3b82f6' : item.method === 'POST' ? '#10b981' : '#f59e0b',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75em'
                    }}>
                      {item.method}
                    </span>
                    <span style={{ fontFamily: 'monospace', flex: 1 }}>{item.endpoint}</span>
                    <span style={{ color: item.response?.error ? '#ef4444' : item.response?.status >= 200 && item.response?.status < 300 ? '#10b981' : '#f59e0b' }}>
                      {item.response?.status || 'Error'}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: '0.8em' }}>
                      {item.response?.duration || 'N/A'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section style={{ marginBottom: 24, borderTop: '2px solid #e5e7eb', paddingTop: 24 }}>
        <h3>Login Logs</h3>
        <p style={{ color: '#6b7280', marginBottom: 16 }}>
          View login activity from the database, sorted by most recent first.
        </p>
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={fetchLoginLogs}
            disabled={loadingLogs}
            style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4, fontWeight: 'bold' }}
          >
            {loadingLogs ? 'Loading...' : 'Load Login Logs'}
          </button>
        </div>
        {loginLogs.length > 0 && (
          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Timestamp</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Display Name</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>User ID</th>
                  <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #e5e7eb' }}>Login Method</th>
                </tr>
              </thead>
              <tbody>
                {loginLogs.map((log) => {
                  const timestamp = log.loginTimestamp;
                  let formattedTimestamp = 'N/A';
                  if (timestamp) {
                    const formatOptions = {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: false
                    };
                    if (timestamp instanceof Date) {
                      formattedTimestamp = timestamp.toLocaleString('en-US', formatOptions);
                    } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
                      formattedTimestamp = timestamp.toDate().toLocaleString('en-US', formatOptions);
                    } else if (typeof timestamp === 'number') {
                      formattedTimestamp = new Date(timestamp).toLocaleString('en-US', formatOptions);
                    }
                  }
                  return (
                    <tr key={log.id}>
                      <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6', fontSize: '0.9em' }}>
                        {formattedTimestamp}
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                        {log.userEmail || <em>(no email)</em>}
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6' }}>
                        {log.displayName || <em>(no name)</em>}
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6', fontFamily: 'monospace', fontSize: '0.85em' }}>
                        {log.userId || <em>(no ID)</em>}
                      </td>
                      <td style={{ padding: 8, borderBottom: '1px solid #f3f4f6', fontSize: '0.9em' }}>
                        {log.loginMethod || 'email'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {loginLogs.length === 0 && !loadingLogs && (
          <div style={{ color: '#6b7280', marginTop: 12 }}>
            No login logs loaded. Click "Load Login Logs" to fetch data.
          </div>
        )}
      </section>
    </div>
  );
}


